
import {
  deleteListing,
  getSellerViewStats,
  hideListingEndpoint,
  initiateOneTimeListingPayment,
  listingBoostedPayment,
  lookupOwnListings,
} from '../../util/api';
import listingLookupCore from '../../util/listingLookupCore';
import { isEmbeddedPaymentEnabled } from '../../util/stripeEmbedded';
import { denormalisedEntities, denormalisedResponseEntities, updatedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { LISTING_SUBSCRIPTION, parse } from '../../util/urlHelpers';
import { ORDER_NEEDS_SELLER_TRANSITIONS } from '../../transactions/transaction';
import { isRunningOut, newOrdersByListing, unreadMessagesByListing } from './listingCounters';
import { CATALOGUE_FILTER, MARKETPLACE_FILTER } from '../../util/catalogueCore';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

export const DELETE_LISTING = 'app/ManageListingsPage/DELETE_LISTING';
export const DELETE_LISTING_LOADING = 'app/ManageListingsPage/DELETE_LISTING_LOADING';
export const DELETE_LISTING_ERROR = 'app/ManageListingsPage/DELETE_LISTING_ERROR';

export const GET_TOTAL_LISTING = 'app/ManageListingsPage/GET_TOTAL_LISTING';

export const GET_TOTAL_LISTING_VIEWS = 'app/ManageListingsPage/GET_TOTAL_LISTING_VIEWS';


// export const GET_TOTAL_SOLD_LISTINGS = 'app/ManageListingsPage/GET_TOTAL_SOLD_LISTINGS';

// export const SET_SOLD_LISTING_IDS = 'app/ManageListingsPage/SET_SOLD_LISTING_IDS';

export const SET_CHIP_COUNTS = 'app/ManageListingsPage/SET_CHIP_COUNTS';

export const SET_TAB_COUNTS = 'app/ManageListingsPage/SET_TAB_COUNTS';

export const SET_VIEW_STATS = 'app/ManageListingsPage/SET_VIEW_STATS';
export const SET_VIEW_STATS_ERROR = 'app/ManageListingsPage/SET_VIEW_STATS_ERROR';


export const GET_DELETED_LISTINGS_COUNT = 'app/ManageListingsPage/GET_DELETED_LISTINGS_COUNT';



export const FETCH_FREE_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_FREE_LISTINGS_REQUEST';
export const FETCH_FREE_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_FREE_LISTINGS_SUCCESS';
export const FETCH_FREE_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_FREE_LISTINGS_ERROR';

export const PAY_LISTING_FEE_REQUEST = 'app/ManageListingsPage/PAY_LISTING_FEE_REQUEST';
export const PAY_LISTING_FEE_SUCCESS = 'app/ManageListingsPage/PAY_LISTING_FEE_SUCCESS';
export const PAY_LISTING_FEE_ERROR = 'app/ManageListingsPage/PAY_LISTING_FEE_ERROR';

export const PAY_LISTING_BOOSTED_PAYMENT_REQUEST = 'app/ManageListingsPage/PAY_LISTING_BOOSTED_PAYMENT_REQUEST';
export const PAY_LISTING_BOOSTED_PAYMENT_SUCCESS = 'app/ManageListingsPage/PAY_LISTING_BOOSTED_PAYMENT_SUCCESS';
export const PAY_LISTING_BOOSTED_PAYMENT_ERROR = 'app/ManageListingsPage/PAY_LISTING_BOOSTED_PAYMENT_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  deleteListingLoading: false,
  deleteListingError: null,
  totalListings: 0,
  allListingsViews: 0,
  totalActiveListings: 0,
  totalClosedListings: 0,
  lowStockCount: 0,
  lowStockIds: [],
  // totalSoldListings: 0,
  // soldListingids: [],
  /* Per-listing chip counts, keyed by listing id — see listingCounters.js. */
  unreadMessagesByListing: {},
  newOrdersByListing: {},
  /* Live counts behind the status tabs (BR-4). null until fetched, so a tab can
   * show nothing rather than a wrong "0" while the counts are in flight. */
  tabCounts: null,
  /* 7-day view figure + trend + per-listing breakdown (BR-5). */
  viewStats: null,
  viewStatsError: null,
  deletedListingsCount: 0,
  freeListings: [],
  fetchFreeListingsInProgress: false,
  fetchFreeListingsError: null,
  sessionURL: null,
  payListingFeeInProgress: false,
  payListingFeeError: null,
  payListingBoostedPaymentSessionURL: null,
  payListingBoostedPaymentInProgress: false,
  payListingBoostedPaymentError: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    case DELETE_LISTING:
      // const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, deleteListingLoading: false };
    case DELETE_LISTING_LOADING:
      return { ...state, deleteListingLoading: true };
    case DELETE_LISTING_ERROR:
      return { ...state, deleteListingLoading: false, deleteListingError: payload };

    case GET_TOTAL_LISTING:
      return { ...state, totalListings: payload };

    case GET_TOTAL_LISTING_VIEWS:
      return {
        ...state, allListingsViews: payload.totalViews, totalActiveListings: payload.activeListingsCount
        , totalClosedListings: payload.closedListingsCount,
        lowStockCount: payload.lowStockCount,
        lowStockIds: payload.lowStockIds,
      };

    // case GET_TOTAL_SOLD_LISTINGS:
    //   return { ...state, totalSoldListings: payload };

    // case SET_SOLD_LISTING_IDS:
    //   return { ...state, soldListingids: payload };

    case SET_CHIP_COUNTS:
      return {
        ...state,
        unreadMessagesByListing: payload.unreadMessagesByListing,
        newOrdersByListing: payload.newOrdersByListing,
      };

    case SET_TAB_COUNTS:
      return { ...state, tabCounts: payload };

    case SET_VIEW_STATS:
      return { ...state, viewStats: payload, viewStatsError: null };

    case SET_VIEW_STATS_ERROR:
      return { ...state, viewStatsError: payload };

    case GET_DELETED_LISTINGS_COUNT:
      return { ...state, deletedListingsCount: payload };


    case FETCH_FREE_LISTINGS_REQUEST:
      return { ...state, fetchFreeListingsInProgress: true, fetchFreeListingsError: null };
    case FETCH_FREE_LISTINGS_SUCCESS:
      return { ...state, fetchFreeListingsInProgress: false, freeListings: payload };
    case FETCH_FREE_LISTINGS_ERROR:
      return { ...state, fetchFreeListingsInProgress: false, fetchFreeListingsError: payload };

    case PAY_LISTING_FEE_REQUEST:
      return { ...state, payListingFeeInProgress: true };
    case PAY_LISTING_FEE_ERROR:
      return { ...state, payListingFeeInProgress: false };
    case PAY_LISTING_FEE_SUCCESS:
      return { ...state, payListingFeeInProgress: false, sessionURL: payload };
    case PAY_LISTING_BOOSTED_PAYMENT_REQUEST:
      return { ...state, payListingBoostedPaymentInProgress: true };
    case PAY_LISTING_BOOSTED_PAYMENT_ERROR:
      return { ...state, payListingBoostedPaymentInProgress: false };
    case PAY_LISTING_BOOSTED_PAYMENT_SUCCESS:
      return { ...state, payListingBoostedPaymentInProgress: false, payListingBoostedPaymentSessionURL: payload };
    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const deleteListingSuccess = transaction => ({
  type: DELETE_LISTING,
  payload: transaction.data,
});

export const deleteListingError = error => ({
  type: DELETE_LISTING_ERROR,
  error: true,
  payload: error,
});

export const getTotalListings = response => ({
  type: GET_TOTAL_LISTING,
  payload: response,
});

export const getTotalListingsViews = response => ({
  type: GET_TOTAL_LISTING_VIEWS,
  payload: response,
});

// export const getTotalSoldListings = response => ({
//   type: GET_TOTAL_SOLD_LISTINGS,
//   payload: response,
// });
// export const setSoldListingIds = ids => ({
//   type: SET_SOLD_LISTING_IDS,
//   payload: ids,
// });

export const setChipCounts = (unreadMessagesByListing, newOrdersByListing) => ({
  type: SET_CHIP_COUNTS,
  payload: { unreadMessagesByListing, newOrdersByListing },
});

export const setTabCounts = counts => ({
  type: SET_TAB_COUNTS,
  payload: counts,
});

export const setViewStats = stats => ({
  type: SET_VIEW_STATS,
  payload: stats,
});

export const setViewStatsError = error => ({
  type: SET_VIEW_STATS_ERROR,
  payload: error,
  error: true,
});

export const getDeletedListingsCount = count => ({
  type: GET_DELETED_LISTINGS_COUNT,
  payload: count,
});


export const fetchFreeListingsRequest = () => ({
  type: FETCH_FREE_LISTINGS_REQUEST,
});

export const fetchFreeListingsSuccess = listings => ({
  type: FETCH_FREE_LISTINGS_SUCCESS,
  payload: listings,
});

export const fetchFreeListingsError = error => ({
  type: FETCH_FREE_LISTINGS_ERROR,
  payload: error,
  error: true,
});

export const payListingFeeRequest = () => ({
  type: PAY_LISTING_FEE_REQUEST,
});

export const payListingFeeSuccess = sessionURL => ({
  type: PAY_LISTING_FEE_SUCCESS,
  payload: sessionURL,
});

export const payListingFeeError = error => ({
  type: PAY_LISTING_FEE_ERROR,
  payload: error,
  error: true,
});

export const payListingBoostedPaymentRequest = () => ({
  type: PAY_LISTING_BOOSTED_PAYMENT_REQUEST,
});

export const payListingBoostedPaymentSuccess = sessionURL => ({
  type: PAY_LISTING_BOOSTED_PAYMENT_SUCCESS,
  payload: sessionURL,
});

export const payListingBoostedPaymentError = error => ({
  type: PAY_LISTING_BOOSTED_PAYMENT_ERROR,
  payload: error,
  error: true,
});

/* The params that shape how a listing is returned (images, sparse fields), not
 * which listings match — an exact lookup keeps these and nothing else. */
const isDisplayParam = key =>
  key === 'include' ||
  key.startsWith('fields.') ||
  key.startsWith('limit.') ||
  key.startsWith('imageVariant.');

const pickDisplayParams = params =>
  Object.fromEntries(Object.entries(params || {}).filter(([key]) => isDisplayParam(key)));

/* The response shape of a query that matched nothing. */
const emptyListingsResponse = perPage => ({
  data: { data: [], included: [], meta: { totalItems: 0, totalPages: 1, page: 1, perPage } },
});

/**
 * P25009-512 BR-5/BR-6/BR-11 — a typed listing number or reg plate is an EXACT
 * lookup, not a text search.
 *
 *   listing number  ("cm03367", "WP-CM03367")  → that listing, or "no results"
 *   reg plate       ("abc 123", "ABC-123")      → the seller's listings with it;
 *                   none → the normal text search with the original text, since
 *                   a word like "VOLVO" also looks like a plate
 *   anything else                              → the normal text search
 *
 * The match is found by our server (/api/own-listings/lookup — only it can
 * search a plate) and the listings are then loaded through the seller's own
 * session in one query, so the rows look exactly like the normal list. Every
 * state is searched and the status tab and page are ignored: an exact match is
 * shown wherever it is (C-13). A failed lookup is an error, never a silent
 * fall back to the text search.
 */
const queryOwnListingsByLookup = async ({ lookup, queryParams, sdk }) => {
  const found = await lookupOwnListings(queryParams.keywords);
  const ids = found?.listingIds || [];
  if (ids.length === 0) {
    return lookup.type === 'tireId' ? emptyListingsResponse(queryParams.perPage) : null;
  }
  return sdk.ownListings.query({ ...pickDisplayParams(queryParams), ids, perPage: ids.length });
};

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => async (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));
  const lookup = listingLookupCore.classifyLookup(queryParams?.keywords);

  /* The lookup route answers to the browser's session cookie, which a server
   * render does not send. Routes.js runs loadData again in the browser, which
   * does the lookup; the server render shows the loading rows meanwhile. */
  if (lookup.type !== 'text' && typeof window === 'undefined') {
    return null;
  }

  try {
    const lookupResponse =
      lookup.type !== 'text' ? await queryOwnListingsByLookup({ lookup, queryParams, sdk }) : null;

    const { perPage, ...rest } = queryParams;
    const response = lookupResponse || (await sdk.ownListings.query({ ...rest, perPage }));
    dispatch(addOwnEntities(response));
    dispatch(queryListingsSuccess(response));
    return response;
  } catch (e) {
    dispatch(queryListingsError(storableError(e)));
    throw e;
  }
};

export const getAllListings = () => (dispatch, getState, sdk) => {
  return sdk.ownListings.query({
    page: 1,
    perPage: 1,
    // include: ['images', 'currentStock', 'pub_isHide'],
    // pub_isDeleted: false,
  })
    .then(response => {
      dispatch(getTotalListings(response?.data?.meta?.totalItems))
      // console.log('response', response)
      return response;
    })
    .catch(e => {
      throw e;
    }
    )
}

export const fetchDeletedListingsCount = () => {
  return async (dispatch, getState, sdk) => {
    try {
      const response = await sdk.ownListings.query({
        page: 1,
        perPage: 1,
        pub_isDeleted: true,
      });

      const deletedCount = response?.data?.meta?.totalItems || 0;
      dispatch(getDeletedListingsCount(deletedCount));
      return deletedCount;
    } catch (error) {
      console.error('Error fetching deleted listings count:', error);
      throw error;
    }
  };
};


/* `getAllInquiries` used to live here. It paginated through EVERY sale on the
 * account to collect the set of listing ids that had any transaction at all, to
 * drive a "Transaktioner" tab. BR-4 removes that tab — the tab row is statuses
 * only — and the "new orders" KPI card is how a seller reaches their orders now.
 * Deleting it also removes the most expensive request on the page: on an account
 * with 265 transactions it was three full 100-row round trips to produce one
 * number, and it was mislabelled ("Transaktioner annonser") into the bargain. */

export const getAllListingsViews = () => async (dispatch, getState, sdk) => {
  const perPage = 100;
  let page = 1;
  let totalViews = 0;
  let totalPages = 1;

  let activeListingsCount = 0;
  let closedListingsCount = 0;
  /* Collected as ids, not just a tally, so the "Snart slut i lager" card can
   * actually go somewhere (BR-5: the action cards "link onward"). Sharetribe
   * cannot filter own listings by stock level, so a low-stock VIEW has to be
   * expressed as an explicit id list — and this loop is already reading every
   * listing to total the views, so the ids are free. */
  const lowStockIds = [];

  try {
    while (page <= totalPages) {
      const response = await sdk.ownListings.query({
        page,
        perPage,
        pub_isDeleted: false,
        /* Stock is a relationship, not an attribute, so the low-stock figure
         * needs it included. Sharetribe cannot filter own listings by stock
         * level, so an exact count means looking at every listing — which this
         * loop already does for the view total. */
        include: ['currentStock'],
      });

      const currentListings = response?.data?.data || [];
      const stockById = (response?.data?.included || [])
        .filter(entity => entity.type === 'stock')
        .reduce((acc, entity) => {
          acc[entity.id.uuid] = entity.attributes?.quantity;
          return acc;
        }, {});

      totalViews += currentListings.reduce((sum, listing) => {
        return sum + (listing?.attributes?.publicData?.totalViews || 0);
      }, 0);

      currentListings.forEach(listing => {
        const { state, publicData = {} } = listing.attributes;
        const isHide = publicData.isHide;

        if (state === 'published' && !isHide) {
          activeListingsCount += 1;
        } else if (isHide) {
          closedListingsCount += 1;
        }

        /* Shared rule — see isRunningOut. The dashboard applies the same one, and
         * writing it out here as well is what made the two disagree before. */
        const stockRef = listing.relationships?.currentStock?.data?.id?.uuid;
        if (
          isRunningOut({
            state,
            isHide,
            isDeleted: publicData.isDeleted,
            quantity: stockRef ? stockById[stockRef] : undefined,
          })
        ) {
          lowStockIds.push(listing.id.uuid);
        }
      });

      if (page === 1 && response?.data?.meta?.totalPages) {
        totalPages = response.data.meta.totalPages;
      }

      page += 1;
    }

    const allData = {
      totalViews,
      activeListingsCount,
      closedListingsCount,
      lowStockCount: lowStockIds.length,
      lowStockIds,
    };

    dispatch(getTotalListingsViews(allData));
    // console.log('All Listings Data:', allData);

    return allData;

  } catch (error) {
    console.error('Failed to fetch all listing data:', error);
    throw error;
  }
};

export const fetchFreeListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchFreeListingsRequest());

  return sdk.ownListings
    .query({
      meta_isFreeListing: true,
    })
    .then(response => {
      const listings = response.data.data;
      dispatch(fetchFreeListingsSuccess(listings));
      return listings;
    })
    .catch(e => {
      dispatch(fetchFreeListingsError(storableError(e)));
      throw e;
    });
};


// export const getAllSoldListings = () => {
//   return async (dispatch, getState, sdk) => {
//     const onlyFilter = 'sale';
//     const states = "state/purchased,state/delivered,state/received,state/completed,state/reviewed-by-customer,state/reviewed-by-provider,state/reviewed";
//     const perPage = 100;
//     let allListingIds = [];

//     try {
//       const firstResponse = await sdk.transactions.query({
//         include: ['listing'],
//         only: onlyFilter,
//         states,
//         page: 1,
//         perPage,
//       });
//       console.log('firstResponse',firstResponse)

//       const totalPages = firstResponse?.data?.meta?.totalPages || 1;
//       const totalItems = firstResponse?.data?.meta?.totalItems || 0;

//       const firstPageListingIds = (firstResponse.data?.included || [])
//         .filter(item => item.type === 'listing' && item.attributes?.publicData?.isDeleted === true)
//         .map(listing => listing.id.uuid);

//       allListingIds.push(...firstPageListingIds);
//       for (let page = 2; page <= totalPages; page++) {
//         const response = await sdk.transactions.query({
//           include: ['listing'],
//           only: onlyFilter,
//           states,
//           page,
//           perPage,
//         });
//         const listingIds = (response.data?.included || [])
//           .filter(item => item.type === 'listing' && item.attributes?.publicData?.isDeleted === true)
//           .map(listing => listing.id.uuid);

//         allListingIds.push(...listingIds);
//       }

//       const uniqueListingIds = [...new Set(allListingIds)];
//       //  console.log('new-response',firstResponse)
//       dispatch(getTotalSoldListings(allListingIds.length));
//       dispatch(setSoldListingIds(uniqueListingIds));

//       return uniqueListingIds;
//     } catch (error) {
//       console.error('Error fetching sold listing IDs:', error);
//       throw error;
//     }
//   };
// };


/**
 * The two per-listing chip counts (BR-2), each from a query the server narrows.
 *
 * This replaces `getAllMessages` and `getAllTransactions`, which both pulled a
 * 100-row page of every sale and decided in the browser what counted. Both got
 * the answer wrong, and both were wrong in the direction that costs the seller
 * time:
 *
 *   - messages: it compared each message's timestamp against `providerLastVisit`,
 *     which reports "this sale was never opened" as "unread message". The inbox
 *     filters on the `providerHasUnreadMessages` metadata flag instead, so the
 *     chip and the page it opened disagreed.
 *   - orders: it counted ANY sale the seller had not opened since it last moved,
 *     regardless of state. On WHEELPLACE Test that is 165 of 265 transactions,
 *     against a true figure of 1 — one listing was advertising "38 nya
 *     transaktioner" that were all cancelled or expired.
 *
 * Both queries now filter server-side, which also makes them small: 9 rows and 2
 * rows respectively on that marketplace, instead of two 100-row pages. That
 * matters for the 100-row ceiling too — the old queries could not see past the
 * first page, and an account with more than 100 sales silently lost counts.
 */
export const fetchListingChipCounts = () => async (dispatch, getState, sdk) => {
  try {
    const [unreadResponse, orderResponse] = await Promise.all([
      /* Exactly what /inbox/sales?filter=unread asks for. */
      sdk.transactions.query({
        only: 'sale',
        meta_providerHasUnreadMessages: true,
        include: ['listing'],
        'fields.listing': ['title'],
        perPage: 100,
      }),
      /* The paid-and-waiting allowlist, applied by the API rather than by us. */
      sdk.transactions.query({
        only: 'sale',
        lastTransitions: ORDER_NEEDS_SELLER_TRANSITIONS.join(','),
        include: ['listing'],
        'fields.listing': ['title'],
        perPage: 100,
      }),
    ]);

    dispatch(
      setChipCounts(
        unreadMessagesByListing(unreadResponse?.data?.data || []),
        newOrdersByListing(orderResponse?.data?.data || [])
      )
    );
  } catch (err) {
    /* Silent: the chips are a convenience on a page whose job is managing
     * listings. A failed count renders as 0 and the chip still navigates, which
     * is better than an error banner over the seller's catalogue. */
    console.error('Failed to fetch listing chip counts:', err);
  }
};

/**
 * Live counts for the status tabs (BR-4 / AC-4).
 *
 * Five `perPage: 1` queries read straight off `meta.totalItems`, so no listing
 * data crosses the wire — the count is the whole point. The params are the same
 * ones `getTabParams` uses to filter the list, so a tab's number always describes
 * the rows that tab will show.
 *
 * Deliberately five separate queries rather than one pass over the catalogue:
 * Sharetribe counts server-side, and a seller with 1000 listings would otherwise
 * pay for 10 pages of data to learn 5 numbers.
 */
export const fetchTabCounts = () => async (dispatch, getState, sdk) => {
  const countOf = async params => {
    const response = await sdk.ownListings.query({ ...params, page: 1, perPage: 1 });
    return response?.data?.meta?.totalItems || 0;
  };

  try {
    const [all, active, catalogue, draft, hidden, removed] = await Promise.all([
      countOf({}),
      // P25009-410 — Aktiva are ads; the catalogue has its own tab.
      countOf({ states: 'published', pub_isHide: false, pub_isDeleted: false, ...MARKETPLACE_FILTER }),
      countOf({ pub_isDeleted: false, ...CATALOGUE_FILTER }),
      countOf({ states: 'draft' }),
      countOf({ pub_isHide: true }),
      countOf({ pub_isDeleted: true }),
    ]);

    dispatch(setTabCounts({ all, active, catalogue, draft, hidden, removed }));
  } catch (err) {
    /* Leave tabCounts null — the tabs render without pills rather than with
     * wrong ones. */
    console.error('Failed to fetch tab counts:', err);
  }
};

/**
 * The 7-day view figure, its trend, and the per-listing breakdown (BR-5).
 *
 * Same endpoint the dashboard card uses, so the two pages cannot report different
 * traffic. `byListing` was added to that response by P25009-385 specifically for
 * this page.
 */
export const fetchViewStats = () => async dispatch => {
  try {
    dispatch(setViewStats(await getSellerViewStats()));
  } catch (e) {
    dispatch(setViewStatsError(storableError(e)));
  }
};


export const closeListing = (listingId, deletionReason) => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return deleteListing({ uuid: listingId, deletionReason })
    .then(async responseListing => {
      await sdk.ownListings
        .close({ id: listingId }, { expand: true })
        .then(response => {
          dispatch(closeListingSuccess(response));
          window.location.reload();
          return response;
        })
        .catch(e => {
          dispatch(closeListingError(storableError(e)));
        });
    })
    .catch(e => {
      dispatch(deleteListingError(storableError(e)));
      log.error(e, 'delete-listing-failed', {});
    });

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

/**
 * BR-11 — the seller confirms (or switches) what a listing's price applies to.
 *
 * Writes the sales unit and clears the "unconfirmed" flag in one update, so a
 * listing that has been through this never raises the banner again. The
 * refreshed listing is merged back into the page's own-entities, which is what
 * makes the banner shrink as the seller works through it.
 *
 * @param {UUID}   listingId
 * @param {Object} values  { salesUnit: 'piece'|'set', setSize: number }
 */
export const confirmSalesUnit = (listingId, values) => (dispatch, getState, sdk) => {
  return sdk.ownListings
    .update(
      {
        id: listingId,
        publicData: {
          salesUnit: values.salesUnit,
          setSize: values.setSize,
          salesUnitConfirmed: true,
        },
      },
      { expand: true }
    )
    .then(response => {
      dispatch(addOwnEntities(response));
      return response;
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const searchOwnListings = (queryParams, config, options) => {
  const page = queryParams.page || 1;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const includeDeleted = options?.includeDeleted === true;

  const baseQuery = {
    ...queryParams,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images', 'currentStock', 'pub_isHide'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  if (!includeDeleted && queryParams.pub_isDeleted === undefined) {
    baseQuery.pub_isDeleted = false;
  }

  return queryOwnListings(baseQuery);
};

// Sharetribe ownListings.query maxes at 100 perPage; safety cap on
// total pages keeps the export from running indefinitely if the
// caller passes filters that match the whole catalogue. 10 × 100 =
// 1000 listings is well above the AC's 500-listing minimum and
// covers the largest sellers we've seen.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 10;

/**
 * Fetch every ownListing matching the current tab+filter combo by
 * paginating through ownListings.query until totalPages is reached
 * (or the safety cap kicks in). Returns denormalised entities so the
 * caller can read attributes / publicData / metadata directly.
 *
 * Reuses the same param shape that searchOwnListings emits but drops
 * image variant configs — the CSV export doesn't need image data and
 * skipping it keeps each request small.
 */
export const fetchAllOwnListingsForExport = (queryParams = {}, options = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  const includeDeleted = options?.includeDeleted === true;

  const baseQuery = {
    ...queryParams,
    perPage: EXPORT_PAGE_SIZE,
    include: ['currentStock'],
  };

  if (!includeDeleted && queryParams.pub_isDeleted === undefined) {
    baseQuery.pub_isDeleted = false;
  }

  const collected = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages && page <= EXPORT_MAX_PAGES) {
    // eslint-disable-next-line no-await-in-loop
    const response = await sdk.ownListings.query({ ...baseQuery, page });
    const entities = denormalisedResponseEntities(response);
    collected.push(...entities);
    totalPages = response?.data?.meta?.totalPages || 1;
    page += 1;
  }

  return collected;
};

export const removeListing = listingId => (dispatch, getState, sdk) => {
  dispatch({ type: DELETE_LISTING_LOADING });

  deleteListing(listingId)
    .then(async responseListing => {
      await sdk.ownListings
        .close({ id: listingId }, { expand: true })
        .then(response => {
          dispatch(deleteListingSuccess(responseListing.data));
          window.location.reload();
        })
        .catch(e => {
          dispatch(deleteListingError(storableError(e)));
        });
    })
    .catch(e => {
      dispatch(deleteListingError(storableError(e)));
      log.error(e, 'delete-listing-failed', {});
    });
};

export const hideListing = (listingId, isHide) => async (dispatch, getState, sdk) => {
  dispatch({ type: DELETE_LISTING_LOADING });

  try {
    await hideListingEndpoint({ uuid: listingId, isHide: isHide });
    window.location.reload();
    // dispatch(deleteListingSuccess(responseListing.data));
  } catch (e) {
    dispatch(deleteListingError(storableError(e)));
  }
};

/**
 * P25009-485 BR-2 — "Gör till säljannons": a hidden trade-in becomes a normal,
 * public ad. The trade-in marker goes, the trade-in date stays for history.
 * Only offered when the listing has a price; without one the row opens the
 * wizard's price step instead (`?edit=<id>&sell=1`).
 *
 * Reloads like hideListing, so every count and tab on the page agrees.
 */
export const makeTradeinSellable = listingId => async (dispatch, getState, sdk) => {
  dispatch({ type: DELETE_LISTING_LOADING });
  try {
    await sdk.ownListings.update({ id: listingId, publicData: { isHide: false, isTradein: false } });
    window.location.reload();
  } catch (e) {
    dispatch(deleteListingError(storableError(e)));
  }
};

/**
 * P25009-485 BR-3 — trade-ins the migration script moved out of Förvaring are
 * still drafts: the Integration API cannot publish a draft, only the author's
 * own session can. They carry `metadata.tradeinPublishPending` and are already
 * hidden (isHide), so publishing them here never shows them publicly.
 *
 * Runs once per page load of Dina annonser, quietly: a failure leaves the
 * draft for the next visit.
 *
 * @returns {Promise<number>} how many were published
 */
export const publishMigratedTradeins = () => async (dispatch, getState, sdk) => {
  try {
    const res = await sdk.ownListings.query({ states: ['draft'], perPage: 100 });
    const pending = (res?.data?.data || []).filter(
      l =>
        l.attributes?.metadata?.tradeinPublishPending === true &&
        l.attributes?.publicData?.isTradein === true &&
        l.attributes?.publicData?.isHide === true
    );
    for (const listing of pending) {
      await sdk.ownListings.publishDraft({ id: listing.id });
    }
    return pending.length;
  } catch (e) {
    console.error('Publishing migrated trade-ins failed', e);
    return 0;
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  await Promise.all([
    dispatch(queryOwnListings({
      ...queryParams,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['images', 'currentStock', 'pub_isHide'],
      // pub_isDeleted: false,
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })),
    /* `getAllListings` and `fetchDeletedListingsCount` used to run here, each
     * spending a request to learn one number for a stat box. `fetchTabCounts`
     * returns both — and three more — in five counting queries that carry no
     * listing data, and the numbers it returns are the ones the tabs filter by.
     * The two thunks are kept for callers outside loadData; nothing here needs
     * them. */
    dispatch(getAllListingsViews()),
    // dispatch(getAllSoldListings()),
    dispatch(fetchListingChipCounts()),
    dispatch(fetchTabCounts()),
    dispatch(fetchViewStats()),
  ]);
};


// Thunk
export const payListingFee = (params) => async (dispatch, getState, sdk) => {
  dispatch(payListingFeeRequest());

  try {
    const state = getState();
    const currentUser = state.user.currentUser;
    const userId = currentUser?.id?.uuid;

    const { listingId } = params;

    const subscriptionName = LISTING_SUBSCRIPTION;

    const metadata = {
      subscriptionName,
      listingId,
      sharetribeUserId: userId,
    };
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

    const bodyParams = {
      successURL: `${baseUrl}${currentPath}`,
      cancelURL: typeof window !== 'undefined' ? window.location.href : '',
      metadata,
      subscriptionName,
    };

    const embedded = isEmbeddedPaymentEnabled();
    const response = await initiateOneTimeListingPayment({ ...bodyParams, embedded });
    const { success, sessionURL, clientSecret } = response;

    if (!success) {
      dispatch(payListingFeeError("Something went wrong during payment initiation"));
      return;
    }

    if (embedded) {
      // Embedded: return the clientSecret to the card; no redux URL / redirect.
      return { clientSecret };
    }

    dispatch(payListingFeeSuccess(sessionURL));
    return sessionURL
  } catch (e) {
    console.error(e);
    dispatch(payListingFeeError(storableError(e)));
    throw e;
  }
};


export const payListingBoostedPayment = (params) => async (dispatch, getState, sdk) => {
  dispatch(payListingBoostedPaymentRequest());

  try {
    const state = getState();
    const currentUser = state.user.currentUser;
    const userId = currentUser?.id?.uuid;

    const { listingId } = params;

    const metadata = {
      listingId,
      sharetribeUserId: userId,
    };
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

    const bodyParams = {
      successURL: `${baseUrl}${currentPath}`,
      cancelURL: typeof window !== 'undefined' ? window.location.href : '',
      metadata,
    };

    const embedded = isEmbeddedPaymentEnabled();
    const response = await listingBoostedPayment({ ...bodyParams, embedded });
    const { success, sessionURL, clientSecret } = response;

    if (!success) {
      dispatch(payListingBoostedPaymentError("Something went wrong during payment initiation"));
      return;
    }

    if (embedded) {
      // Embedded: return the clientSecret to the card; no redux URL / redirect.
      return { clientSecret };
    }

    dispatch(payListingBoostedPaymentSuccess(sessionURL));
    return sessionURL
  } catch (e) {
    console.error(e);
    dispatch(payListingBoostedPaymentError(storableError(e)));
    throw e;
  }
};
